/*
 * © 2017 Renishaw plc. All rights reserved.
 * This source file is the confidential property and copyright of Renishaw plc
 * Reproduction or transmission in whole or in part, in any form or
 * by any means, electronic, mechanical or otherwise, is prohibited
 * without the prior written consent of the copyright owner.
 */
import {
  CentralUser,
  MachineStatus,
  TimeSeriesValueCompactValue,
  MachineStateSummary,
  SystemPermissions,
  EntityPermission,
  GUID,
  TimeSeries,
  Machine,
  ProvisionMachine,
  LicenceDetails,
  TimeSeriesLimit,
  Client,
  MachineConfiguration,
  SortDirection,
  FeatureGroup,
  UnitDisplayHint,
  ProcessAction,
  FileInfoForSelectedJob,
  FileDetailsForSelectedJob,
  MyNotification,
  MachineType,
  IPCRunMode,
  MachineConfigurationDetailsPatch,
  GCodeFileDropMachineConfiguration,
  MachineState,
  ISOString,
  JobSummary,
  ProcessUpdatesTableType,
  UniqueMeasurementType,
} from "@centralwebteam/narwhal";
import { NotificationType } from "@centralwebteam/jellyfish";
import { AlertPresentation } from "@/presentation/Alert";
import { Routes } from "./session/routeDefinitions";
import { JobPresentation } from "@/presentation/Job";
import { MeasurementCharacteristicPresentation } from "@/presentation/MeasurementCharacteristic";
import { MeasurementStatistics } from "@/presentation/MeasurementCharacteristic/statistics";
import { LocationUI } from "@/presentation/Location";
import { ProcessUpdatePresentation } from "@/presentation/processUpdates";
import { ViewState } from "@centralwebteam/jellyfish/src/Login/types";
import { Point } from "@/modules/math/Point";
import { SampleRateType } from "@/modules/sampleRate";

// #region app types

/** Translation keys for provisioning tab titles */
export const ProvisionTabs = {
  createNewMachine: "heading-createNewMachine",
  selectExistingMachine: "heading-selectExistingMachine",
  foundMatchedMachine: "heading-foundMatchedMachine",
};

export type MachinePerformance = {
  totalErrorAlerts: number;
  statusOk: { seconds: number; percent: number };
};
export type JobAggregate = {
  completed: number;
  incomplete: number;
  fail: number;
  passAndWarn: number;
  noVerdict: number;
  total: number;
};

export type JobStatsAggregate = {
  name: string;
  total: number;
  completed: number;
  incomplete: number;
  fail: number;
  passAndWarn: number;
  noVerdict: number;
  start?: string;
};
export type FilterKeys =
  | "locationId"
  // Individual machine selections within the Machine (locationId) filter category
  | "machineId"
  | "machineLicensed"
  | "machineType"
  | "machineStatus"
  | "alertLevel"
  | "jobName"
  | "machineName"
  | "userEmail"
  | "verdict"
  | "clientName";
export type Filter = { [key in FilterKeys]: string[] } & {
  metaData: Record<string, string[]>;
};

export const knownStartValues = [
  "1 hour",
  "1 day",
  "3 days",
  "7 days",
] as const;
export type KnownStartValues = (typeof knownStartValues)[number];

export type RefreshMode = "locked" | "refresh";

export type AlertTypeReason = {
  id: number;
  cumulativePercentage: number;
  percentage: number;
  name: string;
  total: number;
  meanTimeBeforeFail?: string;
};

export type MachineAnalysisViewType = "Job" | "Process" | "Machine";
export const additiveViews: MachineAnalysisViewType[] = ["Process", "Machine"];
export const metrologyViews: MachineAnalysisViewType[] = [
  "Job",
  "Process",
  "Machine",
];

export const jobViewTypes = ["Single", "Multi"] as const;
export type JobViewType = (typeof jobViewTypes)[number];
export const measurementPlotTypes = ["actual", "deviation"] as const;
export type MeasurementPlotType = (typeof measurementPlotTypes)[number];
export type FocusedOrLatestJobViewMeasurementType =
  | "actual"
  | "deviation"
  | "error";
export type MeasurementTypeDetail = {
  featureName?: string;
  name: string;
  /** Although this has a plural name, it is a single FeatureGroup */
  featureGroups?: FeatureGroup;
  toleranceType: string;
  toleranceSubType: string;
  active: boolean;
  unit?: string;
  unitType?: string;
  sampleRate?: string;
};

export type CachedMeasurementTypeDetails = {
  jobName: string;
  lastFetched: string;
  measurements: UniqueMeasurementType[];
  from: string;
  to: string;
};

export type TimeSeriesTypeDetail = TimeSeries & {
  active: boolean;
};

export type TimeSeriesDisplayLimits = {
  lowerLimit?: number;
  upperLimit?: number;
};

/** An array of data from the API, along with the `from` and `to` params used to fetch it */
export type DataRecord<T extends any[]> = {
  data: T;
  params: {
    from: string;
    to: string;
  };
};

export type Popup = {
  group?: string;
  id: string;
  /** ID of the origin element for the popup. This may be used to link the popup to its origin where a direct link using a React.Ref is impractical. */
  refId?: string;
  /** [x, y] pixel co-ordinates to override the popup's default position */
  coord?: [number, number];
};

export type JobSummaryView =
  | "Info"
  | "Characteristics (Fail & Warning)"
  | "Files"
  | "More";

export type PopupUpdate = {
  show?: "toggle" | boolean;
  /** Exclusive group; only one popup of the same group can be open at a time. */
  group?: string;
  /** Unique ID for a particular popup */
  id: string;
  /** ID of the origin element for the popup.
   * This may be used to link the popup to its origin where a direct link using a React.Ref is impractical.
   * However, prefer using coord if possible; getting refs by ID can be unreliable. */
  refId?: string;
  /** [x, y] pixel co-ordinates to override the popup's default position */
  coord?: [number, number];
};

export const viewMode = ["NORMAL", "MICRO"] as const;
export type ViewMode = (typeof viewMode)[number];

export type CarouselDataSet = {
  sensor: TimeSeries;
  values: TimeSeriesValueCompactValue[];
  limits: TimeSeriesLimit[];
};

export type JobPerformance = {
  /** Job name */
  name: string;
  /** Median cycle time in seconds */
  cycleTime: number;
  /** Job run count by verdict group */
  count: {
    fail: number;
    warning: number;
    pass: number;
    passAndWarning: number;
    noVerdict: number;
    completed: number;
    incomplete: number;
    total: number;
  };
  /** Job cumulative duration by verdict group */
  duration: {
    fail: number;
    warning: number;
    pass: number;
    noVerdict: number;
    completed: number;
    incomplete: number;
    total: number;
  };
  /** Pass / fail split in percent. Other verdicts are not included, so the total is <= 100. */
  percent: {
    passAndWarning: number;
    fail: number;
    noVerdict: number;
    completed: number;
    incomplete: number;
  };
};

export type JobType = {
  name: string;
  /** IDs of machines running this job.
   * Set used to get unique values.
   * TODO: Revise this, because Redux warns against using Set in state. */
  machines: GUID[];
  /** index of job instance */
  instances: number[];
  /** names of metadata */
  metadataNames: string[];
};

export type JobPerfSortColumn =
  | "Name"
  | "Passes"
  | "Fails"
  | "Jobs"
  | "Time"
  | "Completed"
  | "Incomplete";
export type JobPerfDetailSortColumnMetrology =
  | "Job Start"
  | "Total Toleranced"
  | "Features Passed"
  | "Features Failed"
  | "Job Status"
  | "Job Verdict"
  | "Cycle Time";
export type JobPerfDetailSortColumnAdditive =
  | "Job Start"
  | "Total Layers"
  | "Last Layer"
  | "Progress"
  | "Job Status"
  | "Job Verdict"
  | "Cycle Time";

export type JobPerfMachineJobAggregateSortColumn =
  | "Machine"
  | "Location"
  | "Total Jobs"
  | "Passes"
  | "Fails"
  | "Completed"
  | "Incomplete"
  | "Total Cycle Time"
  | "Median Cycle Time";

export type JobPerfMetadataJobAggregateSortColumn =
  | "Metadata"
  | "Total Jobs"
  | "Passes"
  | "Fails"
  | "Completed"
  | "Incomplete"
  | "Total Cycle Time"
  | "Median Cycle Time";

export type JobPerfState = "Fetching" | "Working" | "Idle";

export type Evaluation = "Verdict" | "Status";
export type DataMetric = "Count" | "Percent";

export type TreeViewSelectionMode = "single" | "multi";
export type NodeType = "machine" | "location";

export type ManageUpsertMode = "modify" | "create";

export const focusedJobTabs = [
  "measurementResults",
  "measurementSummary",
  "timeSeries",
] as const;
export type FocusedJobTab = (typeof focusedJobTabs)[number];

export const sidebarSections = ["jobs", "timeseries", "measurements"] as const;
export type SidebarSection = (typeof sidebarSections)[number];

export type MachinePerfSortColumn =
  | "Name"
  | "Location"
  | "Completed"
  | "Incomplete"
  | "Passes"
  | "Fails"
  | "Jobs"
  | "Time"
  | "AlertErrors"
  | "AlertWarnings";

export type MachinePerfInnerTableSortColumn =
  | "Job Name"
  | "Total Jobs"
  | "Complete"
  | "Incomplete"
  | "Passes"
  | "Fails"
  | "Machine Status"
  | "Errors";

export type ToolOffsetAppliedType = {
  name: string;
  items: ProcessUpdatePresentation[];
};

export type ProcessUpdatesDetailsSortColumn =
  | "Controlled Offset"
  | "Process Information"
  | "Offset Usage"
  | "Offset Value"
  | "Last Offset Adjustment"
  | "Run Mode"
  | "Measured Feature"
  | "Measured Characteristics"
  | "Job Name"
  | "Updated";

export type ProcessUpdatesDetailsInnerTableSortColumn =
  | "Controlled Offset"
  | "Process Information"
  | "Offset Usage"
  | "Offset Value"
  | "Last Offset Adjustment"
  | "Run Mode"
  | "Measured Feature"
  | "Measured Characteristics"
  | "Job Name"
  | "Updated";

export type OffsetAdjustmentTableSortColumn =
  | "Offset Usage"
  | "Offset Adjustment"
  | "Offset Value"
  | "Feature"
  | "Run Mode"
  | "Characteristic"
  | "Job Name"
  | "Updated";

export type ProcessUpdatesPerfSortColumn =
  | "Manufacturing Machine"
  | "Exceeded"
  | "Applied"
  | "Run Mode"
  | "Passes"
  | "Fails"
  | "Job Name"
  | "Measuring Machine"
  | "Updated"
  | "Total Updates";

/** Aggregated ProcessUpdate type for the Process Updates page */
export type ProcessUpdatePerf = {
  name: string;
  targetMachine: {
    name: string;
    type: MachineType | undefined;
  };
  offsets: {
    exceeded: number;
    applied: number;
  };
  runMode: IPCRunMode | undefined;
  characteristics: {
    fails: number;
    passes: number;
  };
  jobName: string;
  measurementVerdict: string | undefined;
  sourceMachine: {
    name: string;
    type: MachineType | undefined;
  };
  time: Date | undefined;
  controlledOffset: string;
  displayName: string;
  totalUpdates: number;
  lastOffsetAdjustment: string | undefined;
  calculatedCount: number;
};
// #endregion

// #region partial state types
export type MachinePerformanceState = {
  machineJobAggregates: Mappy<JobAggregate>;
  machinePerformances: Mappy<MachinePerformance>;
  lastFetched: null | string;
  lastFetchCompleted: Date | null;
  work: "Fetching" | "Idle" | "Working";
  sortColumn: MachinePerfSortColumn;
  sortDirection: SortDirection;
  openMachines: string[];
  innerTableSortColumn: MachinePerfInnerTableSortColumn;
  innerTableSortDirection: SortDirection;
  machineJobStats: Mappy<JobStatsAggregate[]>;
  machineStatuses: Mappy<{ name: string; percentage: number; fill: string }[]>;
  machineAlerts: Mappy<{ count: number; name: string }[]>;
  previousMachineStatus: {
    from: string;
    machineId: string;
    to: string;
    statuses: [MachineState, number][];
  }[];
  previousAlertDetails: {
    alertCount: number;
    machineAlerts: {
      count: number;
      name: string;
    }[];
    from: ISOString;
    to: ISOString;
    machineId: GUID;
  }[];
  jobsInProgress: Record<string, JobSummary | undefined>;
};

export type ProcessUpdatesGroupData = {
  processUpdatesPerf: MappyAbsolute<ProcessUpdatePerf>;
  processUpdateDetails: MappyAbsolute<ProcessUpdatePresentation[]>;
  processUpdateDetailsMore: MappyAbsolute<
    MappyAbsolute<ProcessUpdatePresentation[]>
  >;
};

export type ProcessUpdatesExpandCollapseState = {
  openProcessActions: string[];
  openDetailsList: string[];
};

export type ProcessUpdatesTableSortState = {
  details: {
    sortColumn: ProcessUpdatesDetailsSortColumn;
    sortDirection: SortDirection;
  };
  perf: {
    sortColumn: ProcessUpdatesPerfSortColumn;
    sortDirection: SortDirection;
  };
  innerTable: {
    sortColumn: ProcessUpdatesDetailsInnerTableSortColumn;
    sortDirection: SortDirection;
  };
};

export type ProcessUpdatesGroupedData = {
  characteristic: ProcessUpdatesGroupData;
  job: ProcessUpdatesGroupData;
  machine: ProcessUpdatesGroupData;
};

export type ProcessUpdatesState = {
  processActions: ProcessAction[];
  appliedOffsetEvents: Record<string, ProcessAction>;
  groupedTableData: ProcessUpdatesGroupedData;
  sortState: {
    characteristic: ProcessUpdatesTableSortState;
    job: ProcessUpdatesTableSortState;
    machine: ProcessUpdatesTableSortState;
  };
  lastFetched: null | string;
  work: "Fetching" | "Idle";
  expandCollapseState: {
    characteristic: ProcessUpdatesExpandCollapseState;
    job: ProcessUpdatesExpandCollapseState;
    machine: ProcessUpdatesExpandCollapseState;
  };
  offsetAdjustmentSortColumn: OffsetAdjustmentTableSortColumn;
  offsetAdjustmentSortDirection: SortDirection;
  processUpdatesTableTypeSelected: ProcessUpdatesTableType;
  lastFetchedForRefresh: null | Date;
  applyingOffsetKeys: string[];
};
export type FilterState = {
  draft: Filter;
  active: Filter;
  search: {
    value: string;
  };
};

export type JobFilesState = {
  data: FileInfoForSelectedJob[];
  details: FileDetailsForSelectedJob[];
  downloadState: Record<string, "initial" | "downloading" | "success" | "fail">;
};

export type GlobalState = {
  machines: Machine[];
  machineTypes: string[];
  locations: LocationUI[];
  expandedLocations: string[];
  notification: {
    show: boolean;
    body: string[];
    header: string;
    id: string;
    type: NotificationType;
  };
  dialogue: {
    show: boolean;
    message: string;
    // not serializable
    confirm: () => void;
  };
  popups: Popup[];
  lastFetched: null | Date;
  expandedMachines: string[];
  activeTab: JobSummaryView;
  preferences: {
    pages: string[] | undefined;
    activeFilters?: Filter;
    viewMode?: ViewMode;
    machineAnalysisTab?: MachineAnalysisViewType;
    processUpdatesTab?: ProcessUpdatesTableType;
  };
  files: Record<string, JobFilesState>;
  defaultRoute: Routes;
  isLiveRefreshModeOn: boolean;
  jobSelectedManuallyFromJobsTimeLine: boolean;
};

export type ManageMachineProvisioningState = {
  machines: ProvisionMachine[];
  selectedRegistrationId: GUID;
};

export const manageAssetsSections = ["licences", "locations"] as const;
export type MangeAssetsSection = (typeof manageAssetsSections)[number];
export type ManageAssetsState = {
  singleSelectedNode: string | null;
  locationId: string | null;
  multiSelectedNodes: string[];
  mode: TreeViewSelectionMode;
  openSections: MangeAssetsSection[];
  activeTab: string;
  confirmDeletion: boolean;
  deleteEntityType: "location" | "machine" | "none";
  refreshInProgress: boolean;
  machineConfigurations:
    | Mappy<MachineConfiguration>
    | Mappy<GCodeFileDropMachineConfiguration>;
  createMachine: boolean;
  formDataInvalidated: boolean;
  // cancelled state is to cancel test connection polling, does not show connection status when another machine is selected after clicking on Test Connection button
  connectionStatus:
    | "none"
    | "started"
    | "connected"
    | "failedToConnect"
    | "cancelled";
  connectionStatusBody: string;
  machineEdits?: Partial<Machine>;
  machineConfigEdits?: MachineConfigurationDetailsPatch;
  controllerResponseFromMachine: any;
  connectorInstallerDownloadProgress: {
    fileId: string;
    loaded: number;
    total: number;
  } | null;
};

export const manageNotificationsSections = [
  "machine alerts",
  "machine status",
  "job status",
  "job verdict",
] as const;
export type ManageNotificationsSection =
  (typeof manageNotificationsSections)[number];

export type ManageNotificationsState = {
  notificationSupported: boolean;
  notificationIsActivated: boolean;
  openSections: ManageNotificationsSection[];
  toggledLocationIds: string[];
  activeMachineAlerts: string[];
  activeMachineStatuses: string[];
  activeJobStatuses: string[];
  activeJobVerdicts: string[];
  activeLocations: string[];
  activeMachines: string[];
  notificationValues: MyNotification[];
  toggleManageDevices: boolean;
  selectAllMachinesAndLocations: boolean;
  headingControllers: string[];
};
export const manageSettingsSections = ["language setting"] as const;
export type ManageSettingsSection = (typeof manageSettingsSections)[number];
export type ManageSettingsState = {
  openSections: ManageSettingsSection[];
  languageSelected: string;
};

export type SettingsFormValues = {
  languageSelected: string;
  openSections: ManageSettingsSection[];
};

export type JobPerformanceState = {
  extendedSummaryFetched: boolean;
  metadataNamesList: string[];
  jobTypes: JobType[];
  jobSummaries: JobPresentation[];
  // job type name -> performance
  jobTypePerformances: MappyAbsolute<JobPerformance>;
  // machine id -> job type name -> performance
  machinePerformances: MappyAbsolute<MappyAbsolute<JobPerformance>>;
  // metadata name -> job type name -> performance,
  metadataPerformance: MappyAbsolute<MappyAbsolute<JobPerformance>>;
  openJobs: string[];
  openMachines: string[];
  openMetadataValues: string[];
  activeMetadata: string;
  sortColumn: JobPerfSortColumn;
  sortDirection: SortDirection;
  detailSortColumn:
    | JobPerfDetailSortColumnAdditive
    | JobPerfDetailSortColumnMetrology;
  detailSortDirection: SortDirection;
  machineJobAggregateSortColumn: JobPerfMachineJobAggregateSortColumn;
  machineJobAggregateSortDirection: SortDirection;
  metadataJobAggregateSortColumn: JobPerfMetadataJobAggregateSortColumn;
  metadataJobAggregateSortDirection: SortDirection;
  work: JobPerfState;
  lastFetchedRange: null | [string, string];
  lastFetchedForRefresh: Date | null;
  filter: {
    draft: {
      evaluation: Evaluation;
      dataMetric: DataMetric;
      groupJobBy: string;
    };
    active: {
      evaluation: Evaluation;
      dataMetric: DataMetric;
      groupJobBy: string;
    };
    userChangedGroupByOnPopup: boolean;
  };
};

export type ManageUsersState = {
  users: CentralUser[];
  selectedUserId: string;
  mode: ManageUpsertMode;
  confirmDeletion: boolean;
};

export type ManageConnectorsPresentation = {
  id: string;
  name: string;
  displayName: string;
  productCode: string;
  productName: string;
  version: string;
  targetSystems: [];
};

export type ManageConnectorsState = {
  connectorDetails: ManageConnectorsPresentation[];
};

export type ManageClientsState = {
  clients: Client[];
  selectedClientId: string;
  selectedClientSecret: string | undefined;
  mode: ManageUpsertMode;
  confirmDeletion: boolean;
};
export type CurrentStatusState = {
  machineAlerts: Mappy<AlertPresentation[]>;
  viewMode: ViewMode;
  machineJobSummaries: Mappy<JobPresentation[]>;
  jobMeasurementCharacteristicsLoading: string[];
  jobMeasurementCharacteristics: Mappy<MeasurementCharacteristicPresentation[]>;
  machineCarouselData: Mappy<CarouselDataSet[]>;
  machineUnitHints: Mappy<UnitDisplayHint[]>;
  selectedMachineAlerts: string | undefined;
  cycleData: MappyAbsolute<{
    max: number;
    median: number;
    mean: number;
    min: number;
    numberOfJobs: number;
  }>;
  lastFetchedMachine: null | string;
  lastFetchedCarouselData: null | string;
  lastFetchedCycleTime: null | string;
  fetchingStatus: "Fetching" | "Idle";
  lastFetched: null | string;
  lastFetchedForRefresh: null | Date;
};

// The name suggests that there is a "manage server" page, but there is not (yet).
export type ManageServerState = {
  serverLicenceState:
    | "unknown"
    | "licensed"
    | "unlicensed"
    | "LicenceServiceNotRunning"
    | "UnlicensedServiceRunning"
    | "ConnectionFailedToCentralServer"
    | "IISStartupFailure";
  serverLicenceType: "unknown" | "Unlicensed" | "Cloud" | "On Prem" | "Edge";
  /** Empty string means a perpetual licence. Otherwise, this is a number (as a string) */
  serverLicenceDaysRemaining: string;
  licenceErrorMessage: string;
  licenceRetryErrorMessage: string;
  showFooter: boolean;
};
// #endregion

export type DataLoadingStates = "Fetching" | "Idle";
export const dataLoadingTypes = [
  "data",
  "focusedJobMoreDetails",
  "timeSeriesValues",
  "measurementSeriesValues",
] as const;
export type DataLoadingTypes = (typeof dataLoadingTypes)[number];

export const dataFetchedType = [
  "states",
  "jobs",
  "alerts",
  "timeSeriesValues",
  "measurementSeriesValues",
  "processActions",
  "unitHints",
  "jobMetadata",
] as const;
export type DataFetchedType = (typeof dataFetchedType)[number];

type JobMeta = {
  details: undefined | JobPresentation;
  relatedJobs: Mappy<JobPresentation>;
  moreDetails: undefined | Mappy<string | number | boolean>;
  relatedFocusedJob: {
    sortColumn: Date | undefined;
    sortDirection: "asc" | "desc";
  };
  // stores focused job reduced end date
  // being used in machine analysis page to unfocus focused job when reduced end date is before start date of daterange
  reducedEndDate: Date | undefined;
  // The measurement detail table and the measurement summary table each keep
  // their own sort column and direction, because their columns barely overlap. They are
  // kept in the same row order by `selectSharedMeasurementRowOrder`, which sorts using
  // whichever table `measurementOrderSource` names and hands the resulting key order to
  // both tables. Column keys live in modules/measurementSorting.
  singleJobTable: {
    sortColumn: string;
    sortDirection: "asc" | "desc";
  };
  /** The measurement summary table's own sorting. */
  measurementSummaryTable: {
    sortColumn: string;
    sortDirection: "asc" | "desc";
  };
  /** Which table last sorted, and therefore drives the shared measurement row order. */
  measurementOrderSource: "detail" | "summary";
};

export type State = {
  manageServer: ManageServerState;
  machinePerformance: MachinePerformanceState;
  manageUsers: ManageUsersState;
  manageMyAccount: {
    activeTab: string | undefined;
  };
  manageProvisioning: ManageMachineProvisioningState;
  manageAssets: ManageAssetsState;
  manageNotifications: ManageNotificationsState;
  manageSettings: ManageSettingsState;
  manageConnectors: ManageConnectorsState;
  manageClients: ManageClientsState;
  jobPerformance: JobPerformanceState;
  filter: FilterState;
  global: GlobalState;
  currentStatus: CurrentStatusState;
  processUpdates: ProcessUpdatesState;
  startupState: "started" | "complete";
  cacheLimit: number;
  view: Routes;
  loginView: ViewState;
  params: {
    start: string;
    end: string | null;
    focusedMachineId: null | string;
    focusedJobId: null | string;
    token: null | string;
    umrt: null | string;
  };
  focusedJob: JobMeta;
  latestJob: JobMeta;
  machineAnalysis: {
    statistics: undefined | MeasurementStatistics[];
    totalJobRuns: number;
    calculatingStatistics: boolean;
  };
  focusedMachine: {
    lastFetched: undefined | string;
    lastFetchedForRefresh: Date | null;
    work: {
      data: DataLoadingStates;
      focusedJobMoreDetails: DataLoadingStates;
      timeSeriesValues: DataLoadingStates;
      measurementSeriesValues: DataLoadingStates;
    };
    /**
     * Important! Preserve order start ASC.
     */
    jobs: Mappy<JobPresentation>;
    /**
     * Important! Preserve order start ASC.
     */
    states: Mappy<MachineStatus>;
    /**
     * Important! Preserve order start ASC.
     */
    alerts: Mappy<AlertPresentation>;
    processActions: Mappy<ProcessAction>;
    hints: UnitDisplayHint[];
    jobMetadata: any[];
    jobMetadataStatus: "idle" | "loading" | "loaded";
    /** true if the initial fetch of unit hints is in progress, used to suppress display of data with unknown units */
    awaitingHints: boolean;
    mainMeasurementDetail: string | undefined;
    measurementDetails: Mappy<MeasurementTypeDetail>;
    // Cache of measurement-type details for related jobs to avoid refetching.
    selectedRelatedJobsUniqueMeasurementsCache: Mappy<CachedMeasurementTypeDetails>;
    measurementValues: Mappy<
      DataRecord<MeasurementCharacteristicPresentation[]>
    >;
    measurementPlotType: MeasurementPlotType;
    mainTimeSeriesDetail: string | undefined;
    focusedJobTab: FocusedJobTab;
    isTableViewSectionOpen: boolean;
    timeSeriesLimits: Mappy<DataRecord<TimeSeriesLimit[]>>;
    timeSeriesDetails: Mappy<TimeSeriesTypeDetail>;
    timeSeriesValues: Mappy<DataRecord<TimeSeriesValueCompactValue[]>>;
    timeSeriesSampleRateOverrides: Mappy<SampleRateType>;
    timeSeriesDisplayLimits: Mappy<TimeSeriesDisplayLimits>;
    filters: {
      jobType: string[];
      alertName: string[];
    };
    selectedAlerts: string[];
    alertTypeParetoSelectedReason: AlertTypeReason | undefined;
    stateSummary: MachineStateSummary | undefined;
  };
  userViewPreference: {
    focusedOrLatestJobViewType: JobViewType;
    focusedOrLatestJobViewMeasurementType: FocusedOrLatestJobViewMeasurementType;
    isOutOfToleranceFilterEnabled: boolean;
    machineAnalysisView: MachineAnalysisViewType;
    activeSidebarSections: SidebarSection[];

    filters: {
      sidebarFilters: {
        jobs?: string;
        measurements?: string;
        timeseries?: string;
      };
      measurementFilter: {
        draft: {
          featureName?: string[];
          characteristicName?: string[];
        };
        active: {
          featureName?: string[];
          characteristicName?: string[];
        };
      };
      timeSeriesFilter: {
        draft: {
          groupingName?: string[];
          seriesName?: string[];
        };
        active: {
          groupingName?: string[];
          seriesName?: string[];
        };
      };
      jobFilter: {
        draft: {
          jobName?: string[];
          verdict?: string[];
          status?: string[];
          metaData?: Record<string, string[]>;
        };
        active: {
          jobName?: string[];
          verdict?: string[];
          status?: string[];
          metaData?: Record<string, string[]>;
        };
      };
      measurementSettings: {
        draft: {
          value: MeasurementPlotType;
        };
        active: {
          value: MeasurementPlotType;
        };
      };
    };
  };
  machineSelector: {
    visibility: "collapsed" | "expanded";
    selected: undefined | string;
  };
  aboutPopup: {
    visibility: "collapsed" | "expanded";
  };
  visualisations: {
    machineAnalysis: {
      dimensions: {
        timeline: {
          width: number;
        };
        lineChart: {
          height: number;
        };
      };
      transforms: {
        timeline: {
          x: number;
          k: number;
        };
        yAxes: Mappy<{ y: number; k: number }>;
        yDeviation: { y: number; k: number };
      };
      seriesColours: Mappy<string>;
      isTimeLineFilterPanelOpen: boolean;
      seriesChart: {
        clickedCoordinates: Point | null;
      };
      id: string;
    };
  };
  previousLiveModeToken: KnownStartValues;
  username: string;
  authenticated: boolean;
  authenticating: boolean;
  systemPermissions: SystemPermissions[] | undefined;
  locationEntityPermissions: EntityPermission[];
  assignedLicences: LicenceDetails[];
  unassignedLicences: LicenceDetails[];
};
